import React from 'react'
import { View } from '@components/atoms'
import InputMask from 'react-input-mask'

import {
	useAppSelector,
	useAppDispatch,
	paymentActions,
	PaymentCardType,
	PaymentStateType,
} from '@redux/index'
import { produce } from 'immer'
import dayjs from 'dayjs'

/**
 * 결제 화면의 Card 블록 — Monolith(2안). 구 구현은 `systems/payment/PaymentCard.tsx`.
 *
 * 카드번호 마스크(Amex 분기), 유효기간 보정(월 범위·지난 해 올림), CVC 자리수 — 전부 구
 * 구현 그대로다. 바꾼 것은 입력 껍데기다: `InputMask` 가 MUI TextField 를 감싸던 것을
 * 밑줄 입력으로 바꿨다(마스크는 children 없이 InputMask 자신이 input 을 그리게 둔다).
 *
 * 구 구현의 오류 표시 두 곳이 엉뚱한 메시지를 가리키고 있었다 — 유효기간 월 오류에
 * 카드번호 메시지를, 카드 이름 오류를 CVC 조건으로 띄웠다. 각자 자기 메시지를 쓰게 고쳤다.
 */
export const MonolithPaymentCard = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const card: PaymentCardType = useAppSelector(state => state.payment.paymentCard)
	const errorMessages: PaymentStateType['paymentErrorMessages'] = useAppSelector(
		state => state.payment.paymentErrorMessages,
	)

	// state
	const [isAmex, setIsAmex] = React.useState<boolean>(false)
	const [tempExpirationDate, setTempExpirationDate] = React.useState<string>('')

	React.useEffect(() => {
		if (card.expiredMonth !== '' && card.expiredYear !== '') {
			setTempExpirationDate(card.expiredMonth + card.expiredYear)
		}
	}, [card])

	const onCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value.replace(/\D/g, '')

		const newCard = produce(card, draft => {
			draft.cardNumber = str
		})

		if (str.startsWith('34') || str.startsWith('37')) {
			setIsAmex(true)
		} else {
			setIsAmex(false)
		}

		reduxDispatch(paymentActions.PAYMENT_CARD({ ...newCard }))
	}

	const onExpirationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value.replace(/\D/g, '')
		const newExpiration = str.startsWith('0') || str.startsWith('1') ? str : '0' + str
		const months = [
			'01',
			'02',
			'03',
			'04',
			'05',
			'06',
			'07',
			'08',
			'09',
			'10',
			'11',
			'12',
		]
		const thisYear = dayjs().format('YYYY')
		let tempStr = ''

		if (str.length === 0) {
			tempStr = str
		} else if (str.length === 1) {
			tempStr = str
		} else if (str.length === 2) {
			if (!months.includes(str)) {
				tempStr = months[0]
			} else {
				tempStr = str
			}
		} else if (str.length === 3) {
			tempStr = str
		} else {
			tempStr = str

			const tempMonth = newExpiration.substring(0, 2)
			const month = months.includes(tempMonth) ? tempMonth : '01'
			const tempYear = newExpiration.slice(-2)
			const year =
				Number('20' + tempYear) < Number(thisYear) ? thisYear.slice(-2) : tempYear

			tempStr = month + year

			const newCard = produce(card, draft => {
				draft.expiredMonth = month
				draft.expiredYear = year
			})

			reduxDispatch(paymentActions.PAYMENT_CARD({ ...newCard }))
		}

		setTempExpirationDate(tempStr)
	}

	const onCvcChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value

		const newCard = produce(card, draft => {
			draft.cvc = str
		})

		reduxDispatch(
			paymentActions.PAYMENT_CARD({
				...newCard,
			}),
		)
	}

	const onCardNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value

		const newCard = produce(card, draft => {
			draft.cardName = str
		})

		reduxDispatch(
			paymentActions.PAYMENT_CARD({
				...newCard,
			}),
		)
	}

	const expirationError: string =
		errorMessages.card.expiredMonth !== ''
			? errorMessages.card.expiredMonth
			: errorMessages.card.expiredYear

	return (
		<View className="mnPaySection">
			<View className="mnPaySectionHead">
				<span className="mnKicker">Card</span>
			</View>

			<View className="mnPaySectionBody">
				<View className="mnField">
					<label className="mnLabel" htmlFor="mnPayCardNumber">
						Card number
					</label>
					<InputMask
						id="mnPayCardNumber"
						className={`mnInput ${errorMessages.card.cardNumber !== '' ? 'error' : ''}`}
						mask={isAmex ? '9999 999999 99999' : '9999 9999 9999 9999'}
						maskChar=""
						inputMode="numeric"
						autoComplete="cc-number"
						value={card.cardNumber}
						onChange={onCardNumberChange}
					/>
					{errorMessages.card.cardNumber !== '' && (
						<span className="mnError">{errorMessages.card.cardNumber}</span>
					)}
				</View>

				<View className="mnFormRow2">
					<View className="mnField">
						<label className="mnLabel" htmlFor="mnPayCardExpiration">
							Expiration (MM / YY)
						</label>
						<InputMask
							id="mnPayCardExpiration"
							className={`mnInput ${expirationError !== '' ? 'error' : ''}`}
							mask="99 / 99"
							maskChar=""
							inputMode="numeric"
							autoComplete="cc-exp"
							value={tempExpirationDate}
							onChange={onExpirationChange}
						/>
						{expirationError !== '' && <span className="mnError">{expirationError}</span>}
					</View>

					<View className="mnField">
						<label className="mnLabel" htmlFor="mnPayCardCvc">
							CVC / CVV
						</label>
						<InputMask
							id="mnPayCardCvc"
							className={`mnInput ${errorMessages.card.cvc !== '' ? 'error' : ''}`}
							mask={isAmex ? '9999' : '999'}
							maskChar=""
							inputMode="numeric"
							autoComplete="cc-csc"
							value={card.cvc}
							onChange={onCvcChange}
						/>
						{errorMessages.card.cvc !== '' && (
							<span className="mnError">{errorMessages.card.cvc}</span>
						)}
					</View>
				</View>

				<View className="mnField">
					<label className="mnLabel" htmlFor="mnPayCardName">
						Name on card
					</label>
					<input
						id="mnPayCardName"
						type="text"
						autoComplete="cc-name"
						className={`mnInput ${errorMessages.card.cardName !== '' ? 'error' : ''}`}
						value={card.cardName}
						onChange={onCardNameChange}
					/>
					{errorMessages.card.cardName !== '' && (
						<span className="mnError">{errorMessages.card.cardName}</span>
					)}
				</View>
			</View>
		</View>
	)
}
